import {
  CButton,
  CButtonGroup,
  CCol,
  CFormGroup,
  CInput,
  CLabel,
  CModal,
  CModalBody,
  CModalFooter,
  CModalHeader,
  CModalTitle,
  CRow,
  CSelect,
  CTextarea,
} from "@coreui/react";
import { useEffect, useState } from "react";
import { useSelector } from "react-redux";
import { createItem, getList } from "../../api/generics";
import { formatToCurrency } from "../../currency/format";
import { PYG } from "../../currency/available-currencies";
import TreasuryAccount from "../../models/treasury";
import { RootState } from "../../store";
import { SUCCESS } from "../../utils/constants/tags";
import { errorAlert, warningAlert } from "../utils/messages";

interface TreasuryAdjustmentModalProps {
  show: boolean;
  // Sin cuenta (desde Movimientos) el modal muestra un selector de cuentas.
  account?: TreasuryAccount;
  onCancel: () => void;
  onSuccess: () => void;
}

const TreasuryAdjustmentModal: React.FC<TreasuryAdjustmentModalProps> = ({
  show,
  account,
  onCancel,
  onSuccess,
}) => {
  const companyId = useSelector(
    (state: RootState) => state.company.data.company.id
  );
  const [direction, setDirection] = useState<"in" | "out">("in");
  const [amount, setAmount] = useState<string>("");
  const [reason, setReason] = useState<string>("");
  const [submitting, setSubmitting] = useState(false);
  const [accounts, setAccounts] = useState<TreasuryAccount[]>([]);
  const [selectedAccountId, setSelectedAccountId] = useState<string>("");

  const fetchAccounts = async () => {
    const accountsStatus = await getList<TreasuryAccount>(
      "/treasury/accounts/",
      -1,
      0,
      new Map([
        ["company_id", companyId?.toString()],
        ["active", "true"],
      ])
    );
    if (accountsStatus.status === SUCCESS && accountsStatus.data) {
      setAccounts(accountsStatus.data.items);
    } else {
      warningAlert(accountsStatus.detail ?? "Error desconocido");
    }
  };

  useEffect(() => {
    setDirection("in");
    setAmount("");
    setReason("");
    setSelectedAccountId("");
    if (show && account === undefined) {
      fetchAccounts();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [show, account]);

  const targetAccount =
    account ??
    accounts.find((candidate) => String(candidate.id) === selectedAccountId);

  const onSave = async () => {
    if (targetAccount === undefined) {
      errorAlert("Seleccione una cuenta");
      return;
    }
    const numericAmount = Number(amount);
    if (!numericAmount || numericAmount <= 0) {
      errorAlert("Indique un monto mayor a 0");
      return;
    }
    if (reason.trim() === "") {
      errorAlert("El motivo del ajuste es obligatorio");
      return;
    }
    setSubmitting(true);
    const requestStatus = await createItem(
      `/treasury/accounts/${targetAccount.id}/adjustments/`,
      {
        amount: direction === "in" ? numericAmount : -numericAmount,
        reason: reason.trim(),
      }
    );
    setSubmitting(false);
    if (requestStatus.status !== SUCCESS) {
      errorAlert(requestStatus.detail ?? "Ha ocurrido un error!!");
      return;
    }
    onSuccess();
  };

  return (
    <CModal show={show} onClose={onCancel}>
      <CModalHeader closeButton>
        <CModalTitle>
          Ajuste manual{account ? `: ${account.name}` : ""}
        </CModalTitle>
      </CModalHeader>
      <CModalBody>
        <fieldset disabled={submitting}>
          {account === undefined ? (
            <CFormGroup>
              <CRow>
                <CCol md={3}>
                  <CLabel>Cuenta:</CLabel>
                </CCol>
                <CCol>
                  <CSelect
                    value={selectedAccountId}
                    onChange={(e: React.ChangeEvent<HTMLSelectElement>) =>
                      setSelectedAccountId(e.target.value)
                    }
                  >
                    <option value="" disabled>
                      -----
                    </option>
                    {accounts.map((candidate) => (
                      <option key={candidate.id} value={candidate.id}>
                        {candidate.name}
                      </option>
                    ))}
                  </CSelect>
                </CCol>
              </CRow>
            </CFormGroup>
          ) : (
            <></>
          )}
          {targetAccount !== undefined ? (
            <p>
              Saldo actual:{" "}
              <b>
                {formatToCurrency(Number(targetAccount.balance ?? 0), PYG)}
              </b>
            </p>
          ) : (
            <></>
          )}
          <CFormGroup>
            <CRow>
              <CCol md={3}>
                <CLabel>Tipo:</CLabel>
              </CCol>
              <CCol>
                <CSelect
                  value={direction}
                  onChange={(e: React.ChangeEvent<HTMLSelectElement>) =>
                    setDirection(e.target.value as "in" | "out")
                  }
                >
                  <option value="in">A favor (suma al saldo)</option>
                  <option value="out">En contra (resta del saldo)</option>
                </CSelect>
              </CCol>
            </CRow>
          </CFormGroup>
          <CFormGroup>
            <CRow>
              <CCol md={3}>
                <CLabel>Monto:</CLabel>
              </CCol>
              <CCol>
                <CInput
                  type="number"
                  min={0}
                  value={amount}
                  onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
                    setAmount(e.target.value)
                  }
                />
              </CCol>
            </CRow>
          </CFormGroup>
          <CFormGroup>
            <CRow>
              <CCol md={3}>
                <CLabel>Motivo:</CLabel>
              </CCol>
              <CCol>
                <CTextarea
                  rows={3}
                  value={reason}
                  onChange={(e: React.ChangeEvent<HTMLTextAreaElement>) =>
                    setReason(e.target.value)
                  }
                  placeholder="Obligatorio"
                />
              </CCol>
            </CRow>
          </CFormGroup>
        </fieldset>
      </CModalBody>
      <CModalFooter>
        <CButtonGroup>
          <CButton color="secondary" onClick={onCancel}>
            Cancelar
          </CButton>
          <CButton color="primary" onClick={onSave} disabled={submitting}>
            {submitting ? "Guardando..." : "Guardar ajuste"}
          </CButton>
        </CButtonGroup>
      </CModalFooter>
    </CModal>
  );
};

export default TreasuryAdjustmentModal;
